import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { DropdownContainer } from "../bits/inputs/DropdownContainer";
import { PrimaryButton, SecondaryButton } from "../bits/buttons";
import { css, useTheme } from "@emotion/react";
import { Box } from "@mui/material";
import Papa from "papaparse";
import { trpc } from "../../utils/trpc";

export type UploadSearchSuggestionsFormData = {
  files: File[];
};

type CsvType = {
  searchTerm: string;
  type: "term" | "case" | "index";
  related?: string;
};

type UploadSearchSuggestionsFormProps = {
  onSubmit: (msg: string) => void;
};

export const UploadSearchSuggestionsForm = ({
  onSubmit,
}: UploadSearchSuggestionsFormProps) => {
  const [formData, setFormData] = useState<UploadSearchSuggestionsFormData>({
    files: [],
  });
  const [error, setError] = useState<string>("");
  const theme = useTheme();

  const uploadSearchSuggestions = trpc.uploadSearchSuggestions.useMutation({
    onSuccess: () => {
      onSubmit("Search suggestions uploaded successfully.");
      setFormData((prev) => ({
        ...prev,
        files: [],
      }));
    },
    onError: () =>
      setError(
        "Error uploading search suggestions please check the file format and try again.",
      ),
  });

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (incomingFiles) => {
      setFormData((prev) => ({
        ...prev,
        files: incomingFiles,
      }));
    },
    accept: {
      "text/csv": [".csv"],
    },
    maxFiles: 1,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    onSubmit("");
    if (!formData.files?.length) {
      setError("Please select a file");
      return;
    }
    const fileReader = new FileReader();
    fileReader.addEventListener(
      "load",
      () => {
        if (!fileReader.result) {
          setError("Error reading file");
          return;
        }
        if (typeof fileReader.result !== "string") {
          setError("Error reading file");
          return;
        }
        const parsed = Papa.parse<CsvType>(fileReader.result.trim(), {
          header: true,
        });

        const data = parsed.data.map((row) => ({
          searchTerm: row.searchTerm.trim(),
          type: row.type.trim().toLowerCase() as CsvType["type"],
          related: row.related ? row.related.trim().split(", ") : [],
        }));

        if (parsed.errors.length) {
          console.error(parsed);
          setError("Error parsing file");
          return;
        }

        uploadSearchSuggestions.mutate({
          data,
        });
      },
      false,
    );
    fileReader.addEventListener(
      "error",
      () => {
        setError("Error reading file");
      },
      false,
    );
    fileReader.readAsText(formData.files[0]);
  };

  const downloadExampleFile = () => {
    const element = document.createElement("a");
    const file = new Blob(
      [
        `searchTerm,type,related
Add-Backs,term,"Add-Fronts, Add-Lefts, Add-Rights"
Adjournment of Capital Claims,term,
Agreements,term,Disagreements
Edgar agreements,term,
Xydhias agreements,term,
Pre nuptial agreements,term,
Anton Piller orders,term,
Marital agreement reached abroad,term,
prorogation clause,term,
Appeals,term,
Permission to appeal,term,
Arbitration,term,
Bankruptcy,term,
Bonuses,term,
Bundles,term,
Chattels,term,
Child Maintenance,term,
Norris v Norris,case,
N v F,case,
Vaughan v Vaughan,case,
MAP v MFP,case,
Rapp v Sarre,case,
Davies v Davies,case,
AW v AH & Ors,case,
Quan v Bray & Ors,case,
Joy v Joy -Morancho,case,
MT v MT Amin v Amin,case,
Santi v Santi,case,
Contempt of court proceedings,index,`,
      ],
      {
        type: "text/csv",
      },
    );
    element.href = URL.createObjectURL(file);
    element.download = "exampleSearchSuggestionsFile.csv";
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "flex", flexDirection: "column", gap: 2, paddingTop: 2 }}
    >
      {!formData.files?.length && (
        <DropdownContainer {...getRootProps()}>
          <input {...getInputProps()} />
          <p>
            Drag and drop or click here to upload the search suggestions csv
          </p>
        </DropdownContainer>
      )}
      {!!formData.files?.length && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
          `}
        >
          <p>Selected file:</p>
          {formData.files?.map((image) => (
            <div
              key={image.name}
              css={css`
                display: flex;
                justify-content: space-between;
                width: 100%;
              `}
            >
              <span>{image.name}</span>
              <span>{Math.round(image.size / 1024)} KB</span>
              <PrimaryButton
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    files: [],
                  }));
                }}
              >
                Delete
              </PrimaryButton>
            </div>
          ))}
        </div>
      )}
      <SecondaryButton type="button" onClick={downloadExampleFile}>
        Download example file
      </SecondaryButton>
      <PrimaryButton type="submit">{"Upload Search Suggestions"}</PrimaryButton>
      <p style={{ color: theme.palette.error.main }}>{error}</p>
    </Box>
  );
};
